import { ReactNode, useEffect, useRef } from "react";
import useAuth from "@/modules/common/hooks/auth.hook";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const WithAxios = ({ children }: { children: ReactNode }) => {
  const { refreshUserState, logout, getTokenByKey } = useAuth();
  const refreshPromise = useRef<Promise<any> | null>(null);
  const isRefreshing = useRef(false);

  // Queue for requests that are waiting for token refresh
  const failedQueue: { resolve: Function; reject: Function }[] = [];

  const processQueue = (error: any = null, token: string | null = null) => {
    failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token);
      }
    });
    failedQueue.length = 0; // Clear the queue
  };

  const getRefreshedToken = async () => {
    try {
      // If there's already a refresh in progress, wait for it
      if (refreshPromise.current) {
        return await refreshPromise.current;
      }

      // Start new refresh
      refreshPromise.current = refreshUserState();
      const result = await refreshPromise.current;
      refreshPromise.current = null;
      return result;
    } catch (error) {
      refreshPromise.current = null;
      throw error;
    }
  };

  useEffect(() => {
    // Request interceptor
    const requestInterceptor = axios.interceptors.request.use(
      async (config: ExtendedAxiosRequestConfig) => {
        try {
          // Don't intercept token refresh requests
          if (config.url?.includes("/oauth2/token")) {
            return config;
          }

          // Get current token
          const idToken = getTokenByKey("idToken");
          if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`;
          }

          return config;
        } catch (error) {
          return Promise.reject(error);
        }
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Don't retry refresh token requests or requests that have already been retried
        if (
          !originalRequest ||
          originalRequest._retry ||
          originalRequest.url?.includes("/oauth2/token")
        ) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          originalRequest._retry = true;

          if (!isRefreshing.current) {
            isRefreshing.current = true;

            try {
              const tokens = await getRefreshedToken();
              isRefreshing.current = false;
              processQueue(null, tokens.id_token);

              // Update the failed request with new token
              originalRequest.headers.Authorization = `Bearer ${tokens.id_token}`;
              return axios(originalRequest);
            } catch (refreshError) {
              isRefreshing.current = false;
              processQueue(refreshError, null);
              await logout();
              return Promise.reject(refreshError);
            }
          } else {
            // Wait for the ongoing refresh to complete
            try {
              const token = await new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              });
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axios(originalRequest);
            } catch (queueError) {
              return Promise.reject(queueError);
            }
          }
        }

        return Promise.reject(error);
      }
    );

    // Cleanup
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      processQueue(new Error("Component unmounted"));
    };
  }, [refreshUserState, logout, getTokenByKey]);

  return <>{children}</>;
};

export default WithAxios;
